import React, { useState, useEffect, useRef, useCallback } from "react";
import { toast } from "../../utils/toast";
import {
  getMinBookingLeadHours,
  getMinBookingLeadMs,
  getPickupMinLeadErrorMessage,
  loadMinBookingLeadHours,
} from "../../utils/bookingLeadTime";

export { getPickupMinLeadError } from "../../utils/bookingLeadTime";

const HOURS = [
  "01",
  "02",
  "03",
  "04",
  "05",
  "06",
  "07",
  "08",
  "09",
  "10",
  "11",
  "12",
];
const MINUTES = Array.from({ length: 60 }, (_, i) =>
  i.toString().padStart(2, "0"),
);

export default function DateSelectModal({
  isOpen,
  onConfirm,
  onCancel,
  initialValueRef,
  initialValue = "",
  config = {},
}) {
  const [view, setView] = useState("predefined"); // predefined, calendar
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedHour, setSelectedHour] = useState("");
  const [selectedMin, setSelectedMin] = useState("");
  const [ampm, setAmpm] = useState("");

  const [calDate, setCalDate] = useState(new Date()); // For calendar navigation
  const [leadHours, setLeadHours] = useState(getMinBookingLeadHours);

  useEffect(() => {
    let alive = true;
    loadMinBookingLeadHours().then((hours) => {
      if (alive) setLeadHours(hours);
    });
    return () => {
      alive = false;
    };
  }, []);

  // Setup initial time to current time + configured lead hours when modal opens
  const scrollToValue = useCallback((wheelRef, value) => {
    const wheel = wheelRef.current;
    if (!wheel) return;
    const spans = wheel.querySelectorAll("span");
    let targetSpan = null;
    spans.forEach((span) => {
      if (span.textContent === value) targetSpan = span;
    });
    if (targetSpan) {
      wheel.scrollTop =
        targetSpan.offsetTop -
        wheel.clientHeight / 2 +
        targetSpan.offsetHeight / 2;
    }
  }, []);

  const getMaxDateTime = useCallback(() => {
    if (!config.maxDateStr) return null;
    const d = new Date(config.maxDateStr);
    return isNaN(d.getTime()) ? null : d;
  }, [config.maxDateStr]);

  const applyDateTimeParts = useCallback(
    (d) => {
      if (!d || Number.isNaN(d.getTime())) return;

      setSelectedDate(d);

      let hours = d.getHours();
      const ampmStr = hours >= 12 ? "PM" : "AM";
      hours = hours % 12;
      hours = hours ? hours : 12;
      const hoursStr = hours.toString().padStart(2, "0");
      const minStr = d.getMinutes().toString().padStart(2, "0");

      setSelectedHour(hoursStr);
      setSelectedMin(minStr);
      setAmpm(ampmStr);
      setCalDate(d);

      setTimeout(() => {
        scrollToValue(hourWheelRef, hoursStr);
        scrollToValue(minWheelRef, minStr);
      }, 50);
    },
    [scrollToValue],
  );

  const initializeTime = useCallback(() => {
    const defaultMin = new Date(Date.now() + getMinBookingLeadMs());
    let minDateTime = defaultMin;
    if (config.minDateStr) {
      const configMin = new Date(config.minDateStr);
      if (!isNaN(configMin.getTime())) {
        // Automatically add 1 minute to the onward time to create a strict gap
        const configMinWithGap = new Date(configMin.getTime() + 1 * 60 * 1000);
        minDateTime = configMinWithGap > defaultMin ? configMinWithGap : defaultMin;
      }
    }

    const maxDt = config.maxDateStr ? new Date(config.maxDateStr) : null;
    let d = minDateTime;
    if (config.defaultDateStr) {
      const def = new Date(config.defaultDateStr);
      if (!isNaN(def.getTime())) {
        d = def;
      }
    }
    if (d.getTime() < minDateTime.getTime()) {
      d = new Date(minDateTime);
    }
    if (maxDt && !isNaN(maxDt.getTime()) && d.getTime() > maxDt.getTime()) {
      d = new Date(maxDt);
    }
    applyDateTimeParts(d);
  }, [config.minDateStr, config.maxDateStr, config.defaultDateStr, applyDateTimeParts, leadHours]);

  useEffect(() => {
    const modalEl = document.getElementById("dateSelectModal");
    const handleShown = () => {
      const fromRef =
        initialValueRef?.current != null &&
        String(initialValueRef.current).trim() !== ""
          ? String(initialValueRef.current).trim()
          : "";
      const fromProp =
        initialValue != null && String(initialValue).trim() !== ""
          ? String(initialValue).trim()
          : "";
      const raw = fromRef || fromProp;
      if (raw !== "") {
        const parsed = new Date(raw);
        if (!Number.isNaN(parsed.getTime())) {
          applyDateTimeParts(parsed);
          return;
        }
      }
      initializeTime();
    };
    if (modalEl) {
      modalEl.addEventListener("shown.bs.modal", handleShown);
    }
    return () => {
      if (modalEl) {
        modalEl.removeEventListener("shown.bs.modal", handleShown);
      }
    };
  }, [initializeTime, initialValue, initialValueRef, applyDateTimeParts]);


  const hourWheelRef = useRef(null);
  const minWheelRef = useRef(null);

  const setupWheel = useCallback((wheelRef, items, setSelected) => {
    const wheel = wheelRef.current;
    if (!wheel) return;

    const handleScroll = () => {
      const centerY = wheel.scrollTop + wheel.clientHeight / 2;
      let closest = null;
      let minDist = Infinity;

      const spans = wheel.querySelectorAll("span");
      spans.forEach((item) => {
        const itemCenter = item.offsetTop + item.offsetHeight / 2;
        const dist = Math.abs(centerY - itemCenter);
        if (dist < minDist) {
          closest = item;
          minDist = dist;
        }
      });

      if (closest) {
        // We don't manually toggle classes here anymore to avoid layout thrashing
        // React will handle the 'active' class via the state update
        if (closest.textContent !== wheel.dataset.lastSelected) {
          wheel.dataset.lastSelected = closest.textContent;
          setSelected(closest.textContent);
        }
      }
    };

    wheel.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      wheel.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const handleItemClick = (wheelRef, value, setSelected) => {
    const wheel = wheelRef.current;
    if (!wheel) return;

    const spans = wheel.querySelectorAll("span");
    let targetSpan = null;
    spans.forEach((span) => {
      if (span.textContent === value) {
        targetSpan = span;
      }
    });

    if (targetSpan) {
      wheel.scrollTo({
        top:
          targetSpan.offsetTop -
          wheel.clientHeight / 2 +
          targetSpan.offsetHeight / 2,
        behavior: "smooth",
      });
      setSelected(value);
    }
  };

  useEffect(() => {
    const cleanup1 = setupWheel(hourWheelRef, HOURS, setSelectedHour);
    const cleanup2 = setupWheel(minWheelRef, MINUTES, setSelectedMin);
    return () => {
      if (cleanup1) cleanup1();
      if (cleanup2) cleanup2();
    };
  }, [setupWheel, view]); // Re-setup if view changes and refs re-mount

  const getFormattedDate = () => {
    const months = [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ];
    if (!selectedDate || !selectedHour || !selectedMin || !ampm) return "";
    return `${months[selectedDate.getMonth()]} ${selectedDate.getDate()}, ${selectedDate.getFullYear()}, ${selectedHour}:${selectedMin} ${ampm}`;
  };

  const getMinDateTime = () => {
    const defaultMin = new Date(Date.now() + getMinBookingLeadMs());
    if (config.minDateStr) {
      const configMin = new Date(config.minDateStr);
      if (!isNaN(configMin.getTime())) {
        // Same gap as initializeTime: 1 minute after onward pick-up
        const configMinWithGap = new Date(
          configMin.getTime() + 1 * 60 * 1000,
        );
        return configMinWithGap > defaultMin ? configMinWithGap : defaultMin;
      }
    }
    return defaultMin;
  };

  const handleConfirm = () => {
    // Build a full Date object from selected date + time
    let hour24 = parseInt(selectedHour, 10);
    if (ampm === "PM" && hour24 !== 12) hour24 += 12;
    if (ampm === "AM" && hour24 === 12) hour24 = 0;

    const chosenDateTime = new Date(
      selectedDate.getFullYear(),
      selectedDate.getMonth(),
      selectedDate.getDate(),
      hour24,
      parseInt(selectedMin, 10),
    );

    const minAllowed = getMinDateTime();
    // Picker selects up to minutes only; normalize min threshold to minute precision
    // so user can select the exact visible lead-time slot without adding +1 minute.
    const minAllowedMinute = new Date(minAllowed);
    minAllowedMinute.setSeconds(0, 0);

    if (chosenDateTime < minAllowedMinute) {
      toast.error(
        config.minErrorMessage ||
          (config.minDateStr
            ? "Return pick-up must be after the onward pick-up (minimum 1 minute gap)."
            : getPickupMinLeadErrorMessage()),
      );
      return;
    }

    const maxAllowed = getMaxDateTime();
    if (maxAllowed && chosenDateTime.getTime() > maxAllowed.getTime()) {
      toast.error(
        config.maxErrorMessage ||
          "Return pick-up must be within 12 hours of the onward pick-up time.",
      );
      return;
    }

    if (onConfirm) onConfirm(getFormattedDate());
  };

  // Calendar logic
  const daysInMonth = (year, month) => new Date(year, month + 1, 0).getDate();
  const firstDayOfMonth = (year, month) => new Date(year, month, 1).getDay();

  const changeMonth = (offset) => {
    const nextDate = new Date(
      calDate.getFullYear(),
      calDate.getMonth() + offset,
      1,
    );
    setCalDate(nextDate);
  };

  const renderCalendar = () => {
    const year = calDate.getFullYear();
    const month = calDate.getMonth();
    const days = daysInMonth(year, month);
    const firstDay = firstDayOfMonth(year, month);
    const monthNames = [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ];

    const minDateTime = getMinDateTime();
    const minDateMidnight = new Date(minDateTime.getFullYear(), minDateTime.getMonth(), minDateTime.getDate());
    const maxDateTime = getMaxDateTime();
    const maxDateMidnight = maxDateTime
      ? new Date(
          maxDateTime.getFullYear(),
          maxDateTime.getMonth(),
          maxDateTime.getDate(),
        )
      : null;

    const grid = [];
    // Empty cells for first week
    for (let i = 0; i < firstDay; i++)
      grid.push(<div key={`empty-${i}`} className="cal-day empty"></div>);
    // Days
    for (let d = 1; d <= days; d++) {
      const iterDate = new Date(year, month, d);
      const isPast = iterDate < minDateMidnight;
      const isAfterMax =
        maxDateMidnight && iterDate.getTime() > maxDateMidnight.getTime();
      const isDisabled = isPast || isAfterMax;
      const isSelected =
        selectedDate.getDate() === d &&
        selectedDate.getMonth() === month &&
        selectedDate.getFullYear() === year;
      grid.push(
        <div
          key={d}
          className={`cal-day${isSelected ? " active" : ""}${isDisabled ? " disabled text-muted" : ""}`}
          onClick={() => {
             if (!isDisabled) setSelectedDate(iterDate);
          }}
          style={{
            cursor: isDisabled ? "not-allowed" : "pointer",
            opacity: isDisabled ? 0.4 : 1,
          }}
        >
          {d}
        </div>,
      );
    }

    return (
      <div className="body_text popup_calender">
        <h3>Select date</h3>
        <div className="calendar-view">
          <div className="cal-header">
            <button onClick={() => changeMonth(-1)}>&lt;</button>
            <span>
              {monthNames[month]} {year}
            </span>
            <button onClick={() => changeMonth(1)}>&gt;</button>
          </div>
          <div className="cal-weekdays">
            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((d) => (
              <div key={d}>{d}</div>
            ))}
          </div>
          <div className="cal-grid">{grid}</div>
        </div>
      </div>
    );
  };

  return (
    <div
      className="modal moreInfo_modal fade"
      id="dateSelectModal"
      tabIndex="-1"
      aria-labelledby="dateSelectModalLabel"
      aria-hidden="true"
    >
      <div
        className={`modal-dialog moreInfo_modal-dialog  ${view === "calendar" ? "add_calendar_time_modal-dialog" : "remove_calendar_time_modal-dialog "}`}
      >
        <div className="modal-content moreInfo_modal-content">
          <div className="modal-header moreInfo_modal-header">
            <h1 className="modal-title fs-5" id="dateSelectModalLabel">
              Schedule Date
            </h1>
            <button
              type="button"
              className="btn-close btn-close-black"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body moreInfo_modal-body">
            {config.noteMessage ? (
              <p
                className="mb-3"
                style={{
                  fontSize: 13,
                  lineHeight: 1.45,
                  color: "#8a6d00",
                  background: "#fff8e1",
                  border: "1px solid #ffe082",
                  borderRadius: 8,
                  padding: "8px 12px",
                }}
              >
                {config.noteMessage}
              </p>
            ) : null}
            <div className="body_text">
              <div className="select_day">
                <button
                  className={`day btn btn-outline-light ${selectedDate.toDateString() === new Date().toDateString() ? "active" : ""}`}
                  onClick={() => {
                    if (new Date() >= new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate())) {
                      setSelectedDate(new Date());
                      setView("predefined");
                    }
                  }}
                  style={{ opacity: new Date() < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate()) ? 0.4 : 1, cursor: new Date() < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate()) ? "not-allowed" : "pointer" }}
                  disabled={new Date() < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate())}
                >
                  Today
                </button>
                <button
                  className={`day btn btn-outline-light ${selectedDate.toDateString() === new Date(Date.now() + 86400000).toDateString() ? "active" : ""}`}
                  onClick={() => {
                    if (new Date(Date.now() + 86400000) >= new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate())) {
                      setSelectedDate(new Date(Date.now() + 86400000));
                      setView("predefined");
                    }
                  }}
                  style={{ opacity: new Date(Date.now() + 86400000) < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate()) ? 0.4 : 1, cursor: new Date(Date.now() + 86400000) < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate()) ? "not-allowed" : "pointer" }}
                  disabled={new Date(Date.now() + 86400000) < new Date(getMinDateTime().getFullYear(), getMinDateTime().getMonth(), getMinDateTime().getDate())}
                >
                  Tomorrow
                </button>
                <button
                  className={`day btn btn-outline-light ${view === "calendar" ? "active" : ""}`}
                  onClick={() => setView("calendar")}
                >
                  Select Date
                </button>
              </div>
            </div>

            <div className="time_calendarWrap ">
              {view === "calendar" ? renderCalendar() : null}
              <div className="body_text">
                <h3>Select time</h3>
                <div className="wheel-container d-flex justify-content-center">
                  <div className="wheel hour-wheel" ref={hourWheelRef}>
                    <div className="spacer"></div>
                    {HOURS.map((h) => (
                      <span
                        key={h}
                        className={h === selectedHour ? "active" : ""}
                        onClick={() =>
                          handleItemClick(hourWheelRef, h, setSelectedHour)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {h}
                      </span>
                    ))}
                    <div className="spacer"></div>
                  </div>
                  <div className="wheel min-wheel" ref={minWheelRef}>
                    <div className="spacer"></div>
                    {MINUTES.map((m) => (
                      <span
                        key={m}
                        className={m === selectedMin ? "active" : ""}
                        onClick={() =>
                          handleItemClick(minWheelRef, m, setSelectedMin)
                        }
                        style={{ cursor: "pointer" }}
                      >
                        {m}
                      </span>
                    ))}
                    <div className="spacer"></div>
                  </div>
                  <div className="wheel ampm-wheel">
                    <div className="spacer"></div>
                    <span
                      className={ampm === "AM" ? "active" : ""}
                      onClick={() => setAmpm("AM")}
                      style={{ cursor: "pointer" }}
                    >
                      AM
                    </span>
                    <span
                      className={ampm === "PM" ? "active" : ""}
                      onClick={() => setAmpm("PM")}
                      style={{ cursor: "pointer" }}
                    >
                      PM
                    </span>
                    <div className="spacer"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-footer moreInfo_modal-footer border-0 flex-column">
            <button
              type="button"
              className="btn btn-warning w-100 py-3 rounded-pill text-dark fw-bold"
              data-bs-dismiss="modal"
              onClick={handleConfirm}
            >
              {getFormattedDate()}
            </button>
            <p className="text-secondary small text-center mt-3">
              We recommend booking at least 12 hrs in advance. Last-minute
              bookings are subject to availability.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
